import { cn } from '@/shared/utils/cn';
import { CardProps } from '@/shared/types/card';
import { PropsWithChildren } from 'react';
import { SeparatorProps } from '@/shared/types/separator';

import { Separator } from '@/modules/Separator';

type CardFooterProps = PropsWithChildren<{
  className?: string;
}>;

type CardHeaderProps = PropsWithChildren<{
  className?: string;
}>;

type CardBodyProps = PropsWithChildren<{
  className?: string;
  fullHeight?: boolean;
  scrollable?: boolean;
}>;

type CardSeparatorProps = SeparatorProps;

/**
 * Card - Componente de tarjeta/card versátil
 *
 * Componente de tarjeta que soporta header, body, footer y separadores.
 * Incluye compound components para máxima flexibilidad en la estructura.
 *
 * @example
 * ```tsx
 * // Card simple
 * <Card>
 *   <Card.Header>Título</Card.Header>
 *   <Card.Body>Contenido de la tarjeta</Card.Body>
 * </Card>
 *
 * // Card con footer
 * <Card>
 *   <Card.Header>Título</Card.Header>
 *   <Card.Body>Contenido</Card.Body>
 *   <Card.Footer>
 *     <Button>Acción</Button>
 *   </Card.Footer>
 * </Card>
 *
 * // Card clickeable
 * <Card onClick={() => console.log('clicked')}>
 *   <Card.Body>Contenido clickeable</Card.Body>
 * </Card>
 *
 * // Sin borde (p. ej. dentro de Drawer brand)
 * <Card bordered={false}>
 *   <Card.Body>Contenido</Card.Body>
 * </Card>
 * ```
 *
 * @see {@link CardProps} para la lista completa de props
 * @see {@link Card.Header}, {@link Card.Body}, {@link Card.Footer}, {@link Card.Separator} para compound components
 */
export const Card = ({
  children,
  className,
  fullWidth = false,
  full = false,
  fullHeight = true,
  onClick,
  rounded = true,
  padding = true,
  gap = true,
  bordered = true,
  active = false,
}: CardProps) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={cn(
        'ui-imj',
        'ui-flex ui-w-full ui-flex-col ui-bg-fill ui-shadow-sm',
        bordered && 'ui-border-1 ui-border-default',
        className,
        gap ? 'ui-gap-16' : '',
        padding ? 'ui-p-16' : '',
        rounded ? 'ui-rounded-surface-card' : '',
        fullWidth ? 'ui-w-full' : 'ui-w-fit',
        fullHeight ? 'ui-h-full' : '',
        full ? 'ui-h-full ui-w-full' : '',
        active && bordered && 'ui-ring-brand !ui-border-brand ui-ring-2',
        active && !bordered && 'ui-ring-brand ui-ring-2',
        onClick
          ? cn(
            'ui-cursor-pointer ui-transition-all ui-duration-200 hover:ui-ring-brand hover:ui-ring-2',
            bordered && 'hover:ui-border-brand',
          )
          : '',
      )}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

Card.Header = ({ children, className }: CardHeaderProps) => {
  return <div className={className}>{children}</div>;
};

Card.Body = ({ children, className, fullHeight = false, scrollable = true }: CardBodyProps) => {
  return (
    <div
      className={cn(
        className,
        fullHeight ? 'ui-h-full' : '',
        scrollable ? 'custom-scrollbar ui-overflow-y-auto' : '',
      )}
    >
      {children}
    </div>
  );
};

Card.Footer = ({ children, className }: CardFooterProps) => {
  return <div className={className}>{children}</div>;
};

Card.Separator = ({ className, vertical = false, padding = true }: CardSeparatorProps) => {
  return <Separator vertical={vertical} padding={padding} className={className} />;
};
